/**
 * MetricCard
 *
 * Colorful metric card component with pastel backgrounds and decorative circles.
 * Used in production dashboards for displaying key metrics.
 *
 * Variants align with ThreadHeads branding:
 * - production: Light purple (brand aligned)
 * - shipping: Blue
 * - gst: Gray
 * - total: Mint green (success)
 * - warning: Amber/orange (for redo counts, alerts)
 * - danger: Red (for stuck bins, critical issues)
 * - info: Cyan/teal (for informational metrics like pretreat)
 */

import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type MetricCardVariant = 'production' | 'shipping' | 'gst' | 'total' | 'warning' | 'danger' | 'info';

interface MetricCardProps {
  /** Card variant determines background color */
  variant: MetricCardVariant;
  /** Card title/label */
  title: string;
  /** Main value to display (e.g., "$24,945.90") */
  value: string;
  /** Optional subtitle below value */
  subtitle?: string;
  /** Icon to display */
  icon: LucideIcon;
  /** Optional className for additional styling */
  className?: string;
}

const VARIANT_STYLES: Record<MetricCardVariant, {
  bg: string;
  iconBg: string;
  iconColor: string;
  titleColor: string;
  valueColor: string;
  circleColor: string;
}> = {
  production: {
    bg: 'bg-[hsl(var(--metric-production))]',
    iconBg: 'bg-primary/20',
    iconColor: 'text-primary',
    titleColor: 'text-primary',
    valueColor: 'text-foreground',
    circleColor: 'bg-primary/10',
  },
  shipping: {
    bg: 'bg-[hsl(var(--metric-shipping))]',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-600',
    valueColor: 'text-foreground',
    circleColor: 'bg-blue-500/10',
  },
  gst: {
    bg: 'bg-[hsl(var(--metric-gst))]',
    iconBg: 'bg-gray-500/20',
    iconColor: 'text-gray-600',
    titleColor: 'text-gray-600',
    valueColor: 'text-foreground',
    circleColor: 'bg-gray-500/10',
  },
  total: {
    bg: 'bg-[hsl(var(--metric-total))]',
    iconBg: 'bg-[hsl(var(--production-accent))]/20',
    iconColor: 'text-[hsl(var(--production-accent))]',
    titleColor: 'text-[hsl(var(--production-accent))]',
    valueColor: 'text-foreground',
    circleColor: 'bg-[hsl(var(--production-accent))]/10',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    iconBg: 'bg-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    titleColor: 'text-amber-600 dark:text-amber-400',
    valueColor: 'text-foreground',
    circleColor: 'bg-amber-500/10',
  },
  danger: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    iconBg: 'bg-red-500/20',
    iconColor: 'text-red-600 dark:text-red-400',
    titleColor: 'text-red-600 dark:text-red-400',
    valueColor: 'text-foreground',
    circleColor: 'bg-red-500/10',
  },
  info: {
    bg: 'bg-cyan-50 dark:bg-cyan-950/30',
    iconBg: 'bg-cyan-500/20',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    titleColor: 'text-cyan-600 dark:text-cyan-400',
    valueColor: 'text-foreground',
    circleColor: 'bg-cyan-500/10',
  },
};

export function MetricCard({
  variant,
  title,
  value,
  subtitle,
  icon: Icon,
  className,
}: MetricCardProps) {
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border p-5',
        styles.bg,
        className
      )}
    >
      {/* Decorative circle in top-right */}
      <div
        className={cn(
          'absolute -top-8 -right-8 h-24 w-24 rounded-full',
          styles.circleColor
        )}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon and title */}
        <div className="flex items-center gap-2.5 mb-3">
          <div className={cn('p-2 rounded-lg', styles.iconBg)}>
            <Icon className={cn('h-5 w-5', styles.iconColor)} />
          </div>
          <span className={cn('font-medium', styles.titleColor)}>
            {title}
          </span>
        </div>

        {/* Value */}
        <div className={cn('text-2xl font-bold tabular-nums', styles.valueColor)}>
          {value}
        </div>

        {/* Optional subtitle */}
        {subtitle && (
          <div className="text-sm text-muted-foreground mt-1">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
